import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLang } from "../lang/useLanguage";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const getLocale = () =>
  (localStorage.getItem("language") || "tr") === "en"
    ? "en-US"
    : "tr-TR";

const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString(getLocale(), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
    : "—";
const money = (v) => v != null ? `${Number(v).toLocaleString("tr-TR")} ₺` : "—";

// ─── KONFİGÜRASYON ────────────────────────────────────────────────────────
const DEPARTMENTS = ["Üretim", "Kalite", "Bakım", "Depo", "Satın Alma", "İK", "Muhasebe", "Yönetim", "Dış Ticaret", "Yurt İçi Satış"];
const ROLES = ["operator", "technician", "warehouse", "manager", "admin"];
const ROLE_LABELS = { operator: "Operatör", technician: "Teknisyen", warehouse: "Depo", manager: "Yönetici", admin: "Admin" };

const getDepartmentLabel = (value) => {
  const h = getLang().hr || {};

  const map = {
    "Üretim": h.departments?.production,
    "Uretim": h.departments?.production,

    "Kalite": h.departments?.quality,

    "Bakım": h.departments?.maintenance,
    "Bakim": h.departments?.maintenance,

    "Planlama": h.departments?.planning,

    "Depo": h.departments?.warehouse,
    "Satın Alma": h.departments?.purchasing,
    "İK": h.departments?.hr,
    "Muhasebe": h.departments?.accounting,
    "Yönetim": h.departments?.management,
    "Dış Ticaret": h.departments?.foreignTrade,
    "Yurt İçi Satış": h.departments?.domesticSales,
  };

  return map[value] || value || "—";
};

const getRoleLabel = (value) => {
  const h = getLang().hr || {};
  return h.roles?.[value] || ROLE_LABELS[value] || value || "—";
};

const getTitleLabel = (value) => {
  if (!value) return "—";

  const h = getLang().hr || {};

  const map = {
    "CNC Operatoru": h.titles?.cncOperator,
    "CNC Operatörü": h.titles?.cncOperator,

    "KK Muhendisi": h.titles?.qualityEngineer,
    "KK Mühendisi": h.titles?.qualityEngineer,

    "Uretim Planlama": h.titles?.productionPlanning,
    "Üretim Planlama": h.titles?.productionPlanning,

    "Bakim Teknisyeni": h.titles?.maintenanceTechnician,
    "Bakım Teknisyeni": h.titles?.maintenanceTechnician,

    "Yurt İçi Satış Müdürü": h.titles?.domesticSalesManager,
    "Yurt İçi Satış Temsilcisi": h.titles?.domesticSalesRepresentative,
    "Yurt İçi Satış Uzmanı": h.titles?.domesticSalesSpecialist,
  };

  return map[value] || value;
};

const getShiftLabel = (value) => {
  const h = getLang().hr || {};

  const map = {
    "Gündüz": h.shifts?.day,
    "Akşam": h.shifts?.evening,
    "Gece": h.shifts?.night,
  };

  return map[value] || value || "—";
};

const getEmpStatus = () => {
  const h = getLang().hr || {};

  return {
    active: {
      label: h.employeeStatus?.active,
      color: "#00ff9d",
      bg: "rgba(0,255,157,0.12)"
    },
    inactive: {
      label: h.employeeStatus?.inactive,
      color: "#94a3b8",
      bg: "rgba(148,163,184,0.12)"
    },
    leave: {
      label: h.employeeStatus?.leave,
      color: "#fbbf24",
      bg: "rgba(251,191,36,0.12)"
    },
    terminated: {
      label: h.employeeStatus?.terminated,
      color: "#f87171",
      bg: "rgba(248,113,113,0.12)"
    },
  };
};
const LIGHT_EMP = {
  active:    { color: "#059669", bg: "#ecfdf5" },
  inactive:  { color: "#64748b", bg: "#f8fafc" },
  leave:     { color: "#d97706", bg: "#fffbeb" },
  terminated:{ color: "#dc2626", bg: "#fef2f2" },
};

const getLeaveTypes = () => {
  const h = getLang().hr || {};

  return {
    annual: {
      label: h.leaveTypes?.annual,
      icon: "🌴"
    },
    sick: {
      label: h.leaveTypes?.sick,
      icon: "🏥"
    },
    maternity: {
      label: h.leaveTypes?.maternity,
      icon: "👶"
    },
    unpaid: {
      label: h.leaveTypes?.unpaid,
      icon: "📋"
    },
    excused: {
      label: h.leaveTypes?.excused,
      icon: "📝"
    },
  };
};
const getAttendStatus = () => {
  const h = getLang().hr || {};

  return {
    present: {
      label: h.attendanceStatus?.present,
      color: "#00ff9d"
    },
    absent: {
      label: h.attendanceStatus?.absent,
      color: "#f87171"
    },
    late: {
      label: h.attendanceStatus?.late,
      color: "#fbbf24"
    },
    leave: {
      label: h.attendanceStatus?.leave,
      color: "#60a5fa"
    },
    holiday: {
      label: h.attendanceStatus?.holiday,
      color: "#94a3b8"
    },
  };
};
const scEmp = (s, tn) => {
  const empStatus = getEmpStatus();

  return tn === "light"
    ? (LIGHT_EMP[s] || LIGHT_EMP.active)
    : (empStatus[s] || empStatus.active);
};

// ─── BADGE ────────────────────────────────────────────────────────────────
const EmpBadge = ({ status, themeName }) => {
  const c = scEmp(status, themeName);
  const empStatus = getEmpStatus();
const cfg = empStatus[status] || empStatus.active;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.color, border: `1px solid ${c.color}44`,
      letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.color }} />
      {cfg.label}
    </span>
  );
};

// ─── ÇALIŞAN AVATAR ───────────────────────────────────────────────────────
const Avatar = ({ name, size = 36, t }) => {
  const initials = (name || "?").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
  const colors = ["#3b82f6","#8b5cf6","#ec4899","#f59e0b","#10b981","#ef4444"];
  const color = colors[(name?.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg, ${color}, ${color}88)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 700, color: "#fff", flexShrink: 0,
    }}>{initials}</div>
  );
};

// ─── ÇALIŞAN DETAY MODAL ──────────────────────────────────────────────────
const EmployeeDetailModal = ({ emp, onClose, onEdit, t, themeName }) => {
  const [tab, setTab] = useState("profile");
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payslips, setPayslips] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [r1, r2, r3] = await Promise.all([
          fetch(`${API}/hr/employees/${emp.id}/attendance?days=30`, { headers: headers() }),
          fetch(`${API}/hr/employees/${emp.id}/leaves`, { headers: headers() }),
          fetch(`${API}/hr/employees/${emp.id}/payslips?limit=6`, { headers: headers() }),
        ]);
        if (r1.ok) setAttendance(await r1.json());
        if (r2.ok) setLeaves(await r2.json());
        if (r3.ok) setPayslips(await r3.json());
      } catch {}
    };
    fetchAll();
  }, [emp.id]);

  const tabs = [
    { id: "profile",    label: getLang().hr?.tabs?.profile },
    { id: "attendance", label: getLang().hr?.tabs?.attendance },
    { id: "leaves",     label: getLang().hr?.tabs?.leaves },
    { id: "payslips",   label: getLang().hr?.tabs?.payslips },
  ];

  const attendanceRate = attendance.length > 0
    ? ((attendance.filter(a => a.status === "present" || a.status === "late").length / attendance.length) * 100).toFixed(1)
    : 0;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay,
      backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`,
        borderRadius: 20, width: "100%", maxWidth: 820, maxHeight: "90vh",
        overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", background: t.bg.header }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Avatar name={emp.full_name} size={48} t={t} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text.primary }}>{emp.full_name}</div>
              <div style={{ fontSize: 13, color: t.text.muted, marginTop: 2 }}>
                {getTitleLabel(emp.title || emp.position)} · {getDepartmentLabel(emp.department)}
              </div>
            </div>
            <EmpBadge status={emp.status} themeName={themeName} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => onEdit(emp)} style={{ padding: "8px 14px", borderRadius: 8,
              border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg,
              color: t.accent.blue, cursor: "pointer", fontSize: 12 }}>✏️</button>
            <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8,
              border: `1px solid ${t.border.input}`, background: t.bg.toggle,
              color: t.text.secondary, cursor: "pointer", fontSize: 12 }}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 2, padding: "0 28px",
          borderBottom: `1px solid ${t.border.divider}`, background: t.bg.header }}>
          {tabs.map(tb => (
            <button key={tb.id} onClick={() => setTab(tb.id)} style={{
              padding: "11px 14px", background: "none", border: "none", cursor: "pointer",
              color: tab === tb.id ? t.accent.blue : t.text.muted, fontSize: 13, fontWeight: 600,
              borderBottom: `2px solid ${tab === tb.id ? t.accent.blue : "transparent"}`,
              transition: "all 0.2s",
            }}>{tb.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 28, background: t.bg.modal }}>

          {/* PROFİL */}
          {tab === "profile" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14,
                  textTransform: "uppercase", letterSpacing: "0.06em" }}>{getLang().hr?.finalUi?.personalInfo}</div>
                {[
                  ["Ad Soyad", emp.full_name],
                  ["TC Kimlik No", emp.tc_no ? emp.tc_no.slice(0, 3) + "****" + emp.tc_no.slice(-4) : "—"],
                  [getLang().hr?.employeeDetail?.birthDate, fmt(emp.birth_date)],
                  [getLang().hr?.employeeDetail?.phone, emp.phone],
                  [getLang().hr?.employeeDetail?.email, emp.email],
                  ["Adres", emp.address],
                  [getLang().hr?.finalUi?.emergencyContact, emp.emergency_contact],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between",
                    padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                    <span style={{ color: t.text.label }}>{k}</span>
                    <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14,
                    textTransform: "uppercase", letterSpacing: "0.06em" }}>{getLang().hr?.finalUi?.workInfo}</div>
                  {[
                    [getLang().hr?.employeeDetail?.employeeNo, emp.employee_no],
                    [getLang().hr?.employeeDetail?.department, getDepartmentLabel(emp.department)],
                    [getLang().hr?.employeeDetail?.position, getTitleLabel(emp.title || emp.position)],
                    [getLang().hr?.finalUi?.role, getRoleLabel(emp.role)],
                    [getLang().hr?.employeeDetail?.shift, getShiftLabel(emp.shift)],
                    [getLang().hr?.finalUi?.hireDate, fmt(emp.hire_date)],
                    [getLang().hr?.finalUi?.seniorityYears, emp.hire_date ? ((new Date() - new Date(emp.hire_date)) / (365.25 * 24 * 3600 * 1000)).toFixed(1) : "—"],
                    [getLang().hr?.employeeDetail?.manager, emp.manager_name],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between",
                      padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                      <span style={{ color: t.text.label }}>{k}</span>
                      <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                    </div>
                  ))}
                </div>
                {/* Devam özeti */}
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12,
                    textTransform: "uppercase", letterSpacing: "0.06em" }}>{getLang().hr?.employeeDetail?.last30DaysSummary}</div>
                  <div style={{ display: "flex", gap: 10 }}>
                    {[
                      { label: getLang().hr?.employeeDetail?.attendance, value: `${attendanceRate}%`, color: t.accent.green },
                      { label: getLang().hr?.employeeDetail?.day, value: attendance.filter(a => a.status === "present").length, color: t.accent.blue },
                      { label: getLang().hr?.employeeDetail?.late, value: attendance.filter(a => a.status === "late").length, color: t.accent.yellow },
                      { label: getLang().hr?.employeeDetail?.absent, value: attendance.filter(a => a.status === "absent").length, color: t.accent.red },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ flex: 1, textAlign: "center", background: t.bg.tag,
                        borderRadius: 10, padding: "10px 6px" }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
                        <div style={{ fontSize: 10, color: t.text.muted, textTransform: "uppercase" }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* DEVAM TAKİBİ */}
          {tab === "attendance" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Mini takvim - son 30 gün */}
              <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 14 }}>{getLang().hr?.employeeDetail?.last30Days}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {attendance.map((a, i) => {
                    const sc_ = getAttendStatus()[a.status] || getAttendStatus().present;
                    return (
                      <div key={i} title={`${fmt(a.date)} — ${sc_.label}`} style={{
                        width: 28, height: 28, borderRadius: 6,
                        background: `${sc_.color}22`,
                        border: `1px solid ${sc_.color}44`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 700, color: sc_.color,
                        cursor: "default",
                      }}>
                        {new Date(a.date).getDate()}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
                  {Object.entries(getAttendStatus()).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: t.text.muted }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: `${v.color}44`, border: `1px solid ${v.color}` }} />
                      {v.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Devam tablosu */}
              <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: t.bg.theadRow }}>
                      {[
                        getLang().hr?.employeeDetail?.date,
                        getLang().hr?.employeeDetail?.checkIn,
                        getLang().hr?.employeeDetail?.checkOut,
                        getLang().hr?.employeeDetail?.workingHours,
                        getLang().hr?.employeeDetail?.status
                      ].map(h => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11,
                          fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.length === 0
                      ? <tr><td colSpan={5} style={{ textAlign: "center", color: t.text.placeholder, padding: 32 }}>{getLang().hr?.employeeDetail?.noAttendance}</td></tr>
                      : attendance.slice(0, 15).map((a, i) => {
                        const sc_ = getAttendStatus()[a.status] || getAttendStatus().present;
                        return (
                          <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.primary, fontWeight: 600 }}>{fmt(a.date)}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.secondary }}>{a.check_in || "—"}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.secondary }}>{a.check_out || "—"}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: t.accent.blue, fontWeight: 600 }}>
                              {a.work_hours ? `${a.work_hours}h` : "—"}
                            </td>
                            <td style={{ padding: "10px 14px" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                                color: sc_.color, background: `${sc_.color}18` }}>{sc_.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* İZİNLER */}
          {tab === "leaves" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* İzin bakiyesi */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 8 }}>
                {[
                  { label: getLang().hr?.employeeDetail?.annualLeaveAllowance, value: `${emp.annual_leave_days || 14} ${getLang().hr?.employeeDetail?.dayUnit}`, color: t.accent.green },
                  { label: getLang().hr?.employeeDetail?.usedLeave, value: `${emp.used_leave_days || 0} ${getLang().hr?.employeeDetail?.dayUnit}`, color: t.accent.yellow },
                  { label: "Kalan", value: `${(emp.annual_leave_days || 14) - (emp.used_leave_days || 0)} gün`, color: t.accent.blue },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`,
                    borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
                  </div>
                ))}
              </div>
              {leaves.length === 0
                ? <div style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{getLang().hr?.employeeDetail?.noLeave}</div>
                : leaves.map((l, i) => {
                  const typeCfg = getLeaveTypes()[l.leave_type] || getLeaveTypes().annual;
                  return (
                    <div key={i} style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`,
                      borderRadius: 12, padding: "14px 18px",
                      display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ fontSize: 20 }}>{typeCfg.icon}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: t.text.primary }}>{typeCfg.label}</div>
                          <div style={{ fontSize: 12, color: t.text.muted }}>
                            {fmt(l.start_date)} — {fmt(l.end_date)} · {l.days} gün
                          </div>
                        </div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                        color: l.status === "approved" ? t.accent.green : l.status === "pending" ? t.accent.yellow : t.accent.red,
                        background: l.status === "approved" ? t.accent.greenBg : l.status === "pending" ? t.accent.yellowBg : t.accent.redBg,
                      }}>{l.status === "approved"
                          ? getLang().hr?.employeeDetail?.leaveApproved
                          : l.status === "pending"
                            ? getLang().hr?.employeeDetail?.leavePending
                            : getLang().hr?.employeeDetail?.leaveRejected}</span>
                    </div>
                  );
                })}
            </div>
          )}

          {/* BORDRO */}
          {tab === "payslips" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {payslips.length === 0
                ? <div style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{getLang().hr?.employeeDetail?.noPayslip}</div>
                : payslips.map((p, i) => (
                  <div key={i} style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`,
                    borderRadius: 14, padding: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>
                        📄 {p.period || `${p.month}/${p.year}`} {getLang().hr?.employeeDetail?.payslipSuffix}
                      </div>
                      <span style={{ fontSize: 18, fontWeight: 800, color: t.accent.green }}>
                        {money(p.net_salary)}
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                      {[
                        { label: getLang().hr?.employeeDetail?.grossSalary, value: money(p.gross_salary), color: t.accent.blue },
                        { label: getLang().hr?.employeeDetail?.socialSecurityEmployee, value: money(p.sgk_employee), color: t.accent.yellow },
                        { label: "Gelir Vergisi", value: money(p.income_tax), color: t.accent.red },
                        { label: getLang().hr?.employeeDetail?.netSalary, value: money(p.net_salary), color: t.accent.green },
                      ].map(({ label, value, color }) => (
                        <div key={label} style={{ background: t.bg.tag, borderRadius: 8, padding: "8px 12px" }}>
                          <div style={{ fontSize: 10, color: t.text.muted, textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── ÇALIŞAN FORM MODAL ───────────────────────────────────────────────────
const EmployeeFormModal = ({ data, onClose, onSaved, t }) => {
  const isEdit = !!data;
  const [form, setForm] = useState({
    full_name: data?.full_name || "",
    employee_no: data?.employee_no || "",
    tc_no: data?.tc_no || "",
    email: data?.email || "",
    phone: data?.phone || "",
    department: data?.department || "Üretim",
    title: data?.title || data?.position || "",
    role: data?.role || "operator",
    shift: data?.shift || "Gündüz",
    hire_date: data?.hire_date?.slice(0, 10) || "",
    birth_date: data?.birth_date?.slice(0, 10) || "",
    gross_salary: data?.gross_salary || "",
    annual_leave_days: data?.annual_leave_days || 14,
    manager_name: data?.manager_name || "",
    emergency_contact: data?.emergency_contact || "",
    address: data?.address || "",
    status: data?.status || "active",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.full_name || !form.department) {
  setError(getLang().hr?.employeeForm?.requiredNameDepartment);
  return;
}
    setLoading(true); setError("");
    try {
      const url = isEdit ? `${API}/hr/employees/${data.id}` : `${API}/hr/employees`;
      const res = await fetch(url, { method: isEdit ? "PUT" : "POST", headers: headers(), body: JSON.stringify(form) });
      if (!res.ok) {
  const payload = await res.json().catch(() => ({}));
  throw new Error(
    payload.detail ||
    getLang().hr?.employeeForm?.genericError
  );
}
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px",
    background: t.bg.input, border: `1px solid ${t.border.input}`,
    borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box",
  };

  const fields = [
    { label: getLang().hr?.employeeForm?.fullName, k: "full_name" },
    { label: getLang().hr?.employeeForm?.employeeNo, k: "employee_no" },
    { label: getLang().hr?.employeeForm?.nationalId, k: "tc_no" },
    { label: getLang().hr?.employeeForm?.email, k: "email", type: "email" },
    { label: getLang().hr?.employeeForm?.phone, k: "phone" },
    { label: getLang().hr?.employeeForm?.department, k: "department", opts: DEPARTMENTS.map(d => ({ v: d, l: getDepartmentLabel(d) })) },
    { label: getLang().hr?.employeeForm?.positionTitle, k: "title" },
    { label: getLang().hr?.employeeForm?.role, k: "role", opts: ROLES.map(r => ({ v: r, l: getRoleLabel(r) })) },
    {
      label: getLang().hr?.employeeForm?.shift,
      k: "shift",
      opts: [
        { v: "Gündüz", l: getShiftLabel("Gündüz") },
        { v: "Akşam",  l: getShiftLabel("Akşam") },
        { v: "Gece",   l: getShiftLabel("Gece") },
      ]
    },
    { label: getLang().hr?.employeeForm?.status, k: "status", opts: Object.entries(getEmpStatus()).map(([v, c]) => ({ v, l: c.label })) },
    { label: getLang().hr?.employeeForm?.hireDate, k: "hire_date", type: "date" },
    { label: getLang().hr?.employeeForm?.birthDate, k: "birth_date", type: "date" },
    { label: getLang().hr?.employeeForm?.grossSalary, k: "gross_salary", type: "number" },
    { label: getLang().hr?.employeeForm?.annualLeaveDays, k: "annual_leave_days", type: "number" },
    { label: getLang().hr?.employeeForm?.manager, k: "manager_name" },
    { label: getLang().hr?.employeeForm?.emergencyContact, k: "emergency_contact" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: t.bg.overlay,
      backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`,
        borderRadius: 20, width: "100%", maxWidth: 680, maxHeight: "90vh",
        overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
            {isEdit ? getLang().hr?.employeeForm?.editTitle : getLang().hr?.employeeForm?.addTitle}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {fields.map(f => (
              <div key={f.k}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{f.label}</label>
                {f.opts ? (
                  <select value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={inputStyle}>
                    {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : (
                  <input type={f.type || "text"} value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={inputStyle} />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
              textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{getLang().hr?.employeeForm?.address}</label>
            <textarea value={form.address} onChange={e => set("address", e.target.value)}
              rows={2} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          {error && <div style={{ marginTop: 14, padding: "10px 16px", background: t.accent.redBg,
            border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8,
            border: `1px solid ${t.border.input}`, background: "none",
            color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{getLang().hr?.employeeForm?.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{
            padding: "10px 24px", borderRadius: 8, border: "none",
            background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)",
            color: "#fff", cursor: loading ? "default" : "pointer",
            fontSize: 13, fontWeight: 600, boxShadow: loading ? "none" : t.shadow.btn,
          }}>{loading ? getLang().hr?.employeeForm?.saving : isEdit ? getLang().hr?.employeeForm?.update : getLang().hr?.employeeForm?.add}</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// HR.js'e eklenecek yeni bileşenler
// "export default function HR()" satırından HEMEN ÖNCE ekle
// ============================================================

// ─── ÇALIŞMA AYARLARI BİLEŞENİ ───────────────────────────────────────────
const WorkSettingsTab = ({ t }) => {
    const [settings, setSettings] = useState({
        work_start: "08:00",
        work_end: "18:00",
        lunch_break: "60",
        tea_break: "30",
        overtime_multiplier: "1.5",
        saturday_multiplier: "1.5",
        sunday_multiplier: "2.0",
        work_days: "Mon,Tue,Wed,Thu,Fri",
        annual_leave_days: "14",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        fetch(`${API}/work-settings`, { headers: headers() })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.settings) setSettings(s => ({ ...s, ...d.settings })); })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await fetch(`${API}/work-settings/batch`, {
                method: "POST", headers: headers(), body: JSON.stringify(settings),
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch { }
        setSaving(false);
    };

    const inputStyle = {
        width: "100%", padding: "9px 12px",
        background: t.bg.input, border: `1px solid ${t.border.input}`,
        borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box",
    };

    if (loading) return (
        <div style={{ textAlign: "center", padding: "60px 0", color: t.text.muted }}>{getLang().hr?.attendanceUi?.loading}</div>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Mesai Saatleri */}
            <div style={{
                background: t.bg.card, border: `1px solid ${t.border.card}`,
                borderRadius: 14, padding: 24, boxShadow: t.shadow.card
            }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 20 }}>
                    {getLang().hr?.workSettingsUi?.workHoursTitle}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16 }}>
                    {[
                        { label: getLang().hr?.workSettingsUi?.workStart, key: "work_start", type: "time" },
                        { label: getLang().hr?.workSettingsUi?.workEnd, key: "work_end", type: "time" },
                    ].map(f => (
                        <div key={f.key}>
                            <label style={{
                                display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6
                            }}>{f.label}</label>
                            <input type={f.type} value={settings[f.key]}
                                onChange={e => setSettings(s => ({ ...s, [f.key]: e.target.value }))}
                                style={inputStyle} />
                        </div>
                    ))}
                </div>
                <div style={{
                    marginTop: 14, padding: "10px 14px", background: t.accent.blueBg,
                    border: `1px solid ${t.accent.blueBorder}`, borderRadius: 8, fontSize: 12, color: t.accent.blue
                }}>
                    {getLang().hr?.workSettingsUi?.normalWorkPrefix} {settings.work_start} — {settings.work_end}{getLang().hr?.workSettingsUi?.normalWorkBetween}{" "}
                    {getLang().hr?.workSettingsUi?.total} {(() => {
                        try {
                            const [sh, sm] = settings.work_start.split(":").map(Number);
                            const [eh, em] = settings.work_end.split(":").map(Number);
                            return ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                        } catch { return "?"; }
                    })()} {getLang().hr?.workSettingsUi?.hourUnit}
                </div>
            </div>

            {/* Mola Ayarları */}
            <div style={{
                background: t.bg.card, border: `1px solid ${t.border.card}`,
                borderRadius: 14, padding: 24, boxShadow: t.shadow.card
            }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 20 }}>
                    {getLang().hr?.workSettingsUi?.breakSettings}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16 }}>
                    {[
                        { label: getLang().hr?.workSettingsUi?.lunchBreak, key: "lunch_break", type: "number" },
                        { label: getLang().hr?.workSettingsUi?.teaBreak, key: "tea_break", type: "number" },
                    ].map(f => (
                        <div key={f.key}>
                            <label style={{
                                display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6
                            }}>{f.label}</label>
                            <input type={f.type} value={settings[f.key]}
                                onChange={e => setSettings(s => ({ ...s, [f.key]: e.target.value }))}
                                style={inputStyle} min="0" />
                        </div>
                    ))}
                </div>
                <div style={{
                    marginTop: 14, padding: "10px 14px", background: t.accent.yellowBg,
                    border: `1px solid ${t.accent.yellow}44`, borderRadius: 8, fontSize: 12, color: t.accent.yellow
                }}>
                    {getLang().hr?.workSettingsUi?.totalBreak} {(parseInt(settings.lunch_break) || 0) + (parseInt(settings.tea_break) || 0)} {getLang().hr?.workSettingsUi?.minuteUnit}{" "}
                    {getLang().hr?.workSettingsUi?.breakDeductionInfo}
                </div>
            </div>

            {/* Fazla Mesai Ayarları */}
            <div style={{
                background: t.bg.card, border: `1px solid ${t.border.card}`,
                borderRadius: 14, padding: 24, boxShadow: t.shadow.card
            }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 20 }}>
                    {getLang().hr?.workSettingsUi?.overtimeSettings}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
                    {[
                        { label: getLang().hr?.workSettingsUi?.overtimeMultiplier, key: "overtime_multiplier", desc: getLang().hr?.workSettingsUi?.overtimeMultiplierDesc },
                        { label: getLang().hr?.workSettingsUi?.saturdayMultiplier, key: "saturday_multiplier", desc: getLang().hr?.workSettingsUi?.saturdayMultiplierDesc },
                        { label: getLang().hr?.workSettingsUi?.sundayMultiplier, key: "sunday_multiplier", desc: getLang().hr?.workSettingsUi?.sundayMultiplierDesc },
                    ].map(f => (
                        <div key={f.key}>
                            <label style={{
                                display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6
                            }}>{f.label}</label>
                            <input type="number" step="0.1" min="1" value={settings[f.key]}
                                onChange={e => setSettings(s => ({ ...s, [f.key]: e.target.value }))}
                                style={inputStyle} />
                            <div style={{ fontSize: 10, color: t.text.muted, marginTop: 4 }}>{f.desc}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Çalışma Günleri */}
            <div style={{
                background: t.bg.card, border: `1px solid ${t.border.card}`,
                borderRadius: 14, padding: 24, boxShadow: t.shadow.card
            }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 20 }}>
                    {getLang().hr?.workSettingsUi?.workDaysLeave}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16 }}>
                    <div>
                        <label style={{
                            display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                            textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8
                        }}>{getLang().hr?.workSettingsUi?.workDays}</label>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {[
                                { key: "Mon", label: getLang().hr?.workSettingsUi?.days?.Mon }, { key: "Tue", label: getLang().hr?.workSettingsUi?.days?.Tue },
                                { key: "Wed", label: getLang().hr?.workSettingsUi?.days?.Wed }, { key: "Thu", label: getLang().hr?.workSettingsUi?.days?.Thu },
                                { key: "Fri", label: getLang().hr?.workSettingsUi?.days?.Fri }, { key: "Sat", label: getLang().hr?.workSettingsUi?.days?.Sat },
                                { key: "Sun", label: getLang().hr?.workSettingsUi?.days?.Sun },
                            ].map(({ key, label }) => {
                                const isActive = settings.work_days?.includes(key);
                                return (
                                    <button key={key} onClick={() => {
                                        const days = settings.work_days?.split(",").filter(Boolean) || [];
                                        const newDays = isActive ? days.filter(d => d !== key) : [...days, key];
                                        setSettings(s => ({ ...s, work_days: newDays.join(",") }));
                                    }} style={{
                                        padding: "6px 12px", borderRadius: 8, cursor: "pointer",
                                        border: `2px solid ${isActive ? t.accent.blue : t.border.input}`,
                                        background: isActive ? t.accent.blueBg : t.bg.input,
                                        color: isActive ? t.accent.blue : t.text.muted,
                                        fontSize: 12, fontWeight: 700, transition: "all 0.15s",
                                    }}>{label}</button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label style={{
                            display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                            textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6
                        }}>{getLang().hr?.workSettingsUi?.annualLeaveDays}</label>
                        <input type="number" value={settings.annual_leave_days}
                            onChange={e => setSettings(s => ({ ...s, annual_leave_days: e.target.value }))}
                            style={inputStyle} min="0" />
                    </div>
                </div>
            </div>

            {/* Kaydet Butonu */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                {saved && (
                    <div style={{
                        padding: "10px 16px", background: t.accent.greenBg,
                        border: `1px solid ${t.accent.green}44`, borderRadius: 8,
                        color: t.accent.green, fontSize: 13, fontWeight: 600
                    }}>
                        {getLang().hr?.workSettingsUi?.saved}
                    </div>
                )}
                <button onClick={handleSave} disabled={saving} style={{
                    padding: "12px 28px", borderRadius: 10, border: "none",
                    background: saving ? t.bg.tag : "linear-gradient(135deg,#059669,#10b981)",
                    color: "#fff", cursor: saving ? "default" : "pointer",
                    fontSize: 14, fontWeight: 600, boxShadow: saving ? "none" : "0 4px 14px rgba(16,185,129,0.3)",
                }}>{saving ? getLang().hr?.workSettingsUi?.saving : getLang().hr?.workSettingsUi?.save}</button>
            </div>
        </div>
    );
};

// ─── PUANTAJ BİLEŞENİ ─────────────────────────────────────────────────────
const TimesheetTab = ({ t, employees }) => {
    const now = new Date();
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const [timesheet, setTimesheet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addForm, setAddForm] = useState({
        personnel_id: "", work_date: new Date().toISOString().slice(0, 10),
        check_in: "08:00", check_out: "18:00", status: "present", notes: "",
    });
    const [addLoading, setAddLoading] = useState(false);
    const [addResult, setAddResult] = useState(null);

    const MONTHS = getLang().hr?.attendanceUi?.months || [];

    const fetchTimesheet = useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/attendance/timesheet/${month}/${year}`, { headers: headers() });
            if (r.ok) setTimesheet(await r.json());
        } catch { }
        setLoading(false);
    }, [month, year]);

    useEffect(() => { fetchTimesheet(); }, [fetchTimesheet]);

    const handleAddAttendance = async () => {
        setAddLoading(true); setAddResult(null);
        try {
            const res = await fetch(`${API}/attendance/record`, {
                method: "POST", headers: headers(), body: JSON.stringify(addForm),
            });
            const d = await res.json();
            if (res.ok) {
                setAddResult({ ok: true, ...d });
                fetchTimesheet();
            } else {
                setAddResult({ ok: false, error: d.detail });
            }
        } catch (e) { setAddResult({ ok: false, error: e.message }); }
        setAddLoading(false);
    };

    const STATUS_COLORS = {
        present: "#00ff9d", absent: "#f87171", late: "#fbbf24",
        leave: "#60a5fa", holiday: "#94a3b8",
    };
    const STATUS_LABELS = {
    present: getLang().hr?.finalUi?.shortAttendance?.present,
    absent: getLang().hr?.finalUi?.shortAttendance?.absent,
    late: getLang().hr?.finalUi?.shortAttendance?.late,
    leave: getLang().hr?.finalUi?.shortAttendance?.leave,
    holiday: getLang().hr?.finalUi?.shortAttendance?.holiday,
};

    const inputStyle = {
        width: "100%", padding: "8px 12px",
        background: t.bg.input, border: `1px solid ${t.border.input}`,
        borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box",
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Kontroller */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{
                        padding: "9px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`,
                        borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none"
                    }}>
                        {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                    </select>
                    <select value={year} onChange={e => setYear(Number(e.target.value))} style={{
                        padding: "9px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`,
                        borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none"
                    }}>
                        {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button onClick={fetchTimesheet} style={{
                        padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                        background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", fontSize: 13, fontWeight: 600,
                    }}>{getLang().hr?.attendanceUi?.viewTimesheet}</button>
                </div>
                <button onClick={() => setShowAddModal(true)} style={{
                    padding: "9px 16px", borderRadius: 8, cursor: "pointer",
                    border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg,
                    color: t.accent.blue, fontSize: 13, fontWeight: 600,
                }}>{getLang().hr?.attendanceUi?.addAttendance}</button>
                <button onClick={async () => {
                    await fetch(`${API}/attendance/mark-absent`, { method: "POST", headers: headers() });
                    fetchTimesheet();
                }} style={{
                    padding: "9px 16px", borderRadius: 8, cursor: "pointer",
                    border: `1px solid ${t.accent.red}44`, background: t.accent.redBg,
                    color: t.accent.red, fontSize: 13, fontWeight: 600,
                }}>{getLang().hr?.attendanceUi?.markMissingAbsent}</button>
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: t.text.muted }}>
                        <div style={{
                            width: 20, height: 20, borderRadius: 4, background: `${STATUS_COLORS[k]}22`,
                            border: `1px solid ${STATUS_COLORS[k]}`, display: "flex", alignItems: "center",
                            justifyContent: "center", fontSize: 9, fontWeight: 700, color: STATUS_COLORS[k]
                        }}>{v}</div>
                        {k === "present"
                        ? getLang().hr?.attendanceUi?.statusPresent
                        : k === "absent"
                          ? getLang().hr?.attendanceUi?.statusAbsent
                          : k === "late"
                            ? getLang().hr?.attendanceUi?.statusLate
                            : k === "leave"
                              ? getLang().hr?.attendanceUi?.statusLeave
                              : getLang().hr?.attendanceUi?.statusHoliday}
                    </div>
                ))}
            </div>

            {/* Puantaj Tablosu */}
            {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0", color: t.text.muted }}>{getLang().hr?.attendanceUi?.loading}</div>
            ) : !timesheet ? (
                <div style={{ textAlign: "center", padding: "60px 0", color: t.text.placeholder }}>
                    {getLang().hr?.attendanceUi?.selectMonthHint}
                </div>
            ) : (
                <div style={{
                    background: t.bg.card, border: `1px solid ${t.border.card}`,
                    borderRadius: 14, overflow: "auto", boxShadow: t.shadow.card
                }}>
                    <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
                        <thead>
                            <tr style={{ background: t.bg.theadRow }}>
                                <th style={{
                                    padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700,
                                    color: t.text.label, textTransform: "uppercase", position: "sticky", left: 0,
                                    background: t.bg.theadRow, zIndex: 1, minWidth: 160
                                }}>{getLang().hr?.attendanceUi?.personnel}</th>
                                {Array.from({ length: timesheet.days_in_month }, (_, i) => i + 1).map(d => (
                                    <th key={d} style={{
                                        padding: "8px 4px", textAlign: "center", fontSize: 10,
                                        fontWeight: 700, color: t.text.label, minWidth: 30
                                    }}>{d}</th>
                                ))}
                                <th style={{
                                    padding: "10px 12px", textAlign: "center", fontSize: 10,
                                    fontWeight: 700, color: t.text.label, whiteSpace: "nowrap"
                                }}>{getLang().hr?.attendanceUi?.workDays}</th>
                                <th style={{
                                    padding: "10px 12px", textAlign: "center", fontSize: 10,
                                    fontWeight: 700, color: t.text.label, whiteSpace: "nowrap"
                                }}>{getLang().hr?.attendanceUi?.hours}</th>
                                <th style={{
                                    padding: "10px 12px", textAlign: "center", fontSize: 10,
                                    fontWeight: 700, color: t.accent.yellow, whiteSpace: "nowrap"
                                }}>{getLang().hr?.attendanceUi?.overtime}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(timesheet.timesheet || []).map((row, ri) => (
                                <tr key={ri} style={{
                                    borderTop: `1px solid ${t.border.subtle}`,
                                    background: ri % 2 === 0 ? t.bg.rowAlt : t.bg.row
                                }}>
                                    <td style={{
                                        padding: "8px 14px", position: "sticky", left: 0,
                                        background: ri % 2 === 0 ? t.bg.rowAlt : t.bg.row, zIndex: 1
                                    }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: t.text.primary }}>{row.full_name}</div>
                                        <div style={{ fontSize: 10, color: t.text.muted }}>{row.department}</div>
                                    </td>
                                    {Array.from({ length: timesheet.days_in_month }, (_, i) => i + 1).map(d => {
                                        const day = row.days?.[d];
                                        const status = day?.status;
                                        const color = STATUS_COLORS[status] || t.border.subtle;
                                        const label = STATUS_LABELS[status] || "";
                                        return (
                                            <td key={d} style={{ padding: "4px 2px", textAlign: "center" }}>
                                                {status ? (
                                                    <div style={{
                                                        width: 22, height: 22, borderRadius: 4, margin: "0 auto",
                                                        background: `${color}22`, border: `1px solid ${color}55`,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        fontSize: 8, fontWeight: 800, color
                                                    }}>
                                                        {label}
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        width: 22, height: 22, margin: "0 auto",
                                                        borderRadius: 4, background: t.bg.tag
                                                    }} />
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td style={{
                                        padding: "8px 12px", textAlign: "center", fontWeight: 700,
                                        color: t.accent.blue, fontSize: 13
                                    }}>{row.present_days}</td>
                                    <td style={{
                                        padding: "8px 12px", textAlign: "center", fontWeight: 700,
                                        color: t.accent.green, fontSize: 13
                                    }}>{row.total_worked_hours}</td>
                                    <td style={{
                                        padding: "8px 12px", textAlign: "center", fontWeight: 700,
                                        color: t.accent.yellow, fontSize: 13
                                    }}>{row.total_overtime_hours}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Devam Kaydı Ekle Modal */}
            {showAddModal && (
                <div style={{
                    position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay,
                    backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20
                }}
                    onClick={() => setShowAddModal(false)}>
                    <div style={{
                        background: t.bg.modal, border: `1px solid ${t.border.default}`,
                        borderRadius: 20, width: "100%", maxWidth: 480, padding: 28, boxShadow: t.shadow.modal
                    }}
                        onClick={e => e.stopPropagation()}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: t.text.primary, marginBottom: 20 }}>
                            {getLang().hr?.attendanceUi?.addAttendance}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {[
                                {
                                    label: getLang().hr?.attendanceUi?.personnel, key: "personnel_id", type: "select",
                                    opts: employees.map(e => ({ v: e.id, l: e.full_name }))
                                },
                                { label: getLang().hr?.attendanceUi?.date, key: "work_date", type: "date" },
                                { label: getLang().hr?.attendanceUi?.checkInTime, key: "check_in", type: "time" },
                                { label: getLang().hr?.attendanceUi?.checkOutTime, key: "check_out", type: "time" },
                                {
                                    label: getLang().hr?.attendanceUi?.status, key: "status", type: "select", opts: [
                                        { v: "present", l: getLang().hr?.attendanceUi?.statusPresent },
                                        { v: "absent", l: getLang().hr?.attendanceUi?.statusAbsent },
                                        { v: "late", l: getLang().hr?.attendanceUi?.statusLate },
                                        { v: "leave", l: getLang().hr?.attendanceUi?.statusLeave },
                                    ]
                                },
                                { label: getLang().hr?.finalUi?.notes, key: "notes", type: "text" },
                            ].map(f => (
                                <div key={f.key}>
                                    <label style={{
                                        display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                                        textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5
                                    }}>{f.label}</label>
                                    {f.type === "select" ? (
                                        <select value={addForm[f.key]}
                                            onChange={e => setAddForm(p => ({ ...p, [f.key]: e.target.value }))}
                                            style={inputStyle}>
                                            <option value="">{getLang().hr?.finalUi?.select}</option>
                                            {f.opts?.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                                        </select>
                                    ) : (
                                        <input type={f.type} value={addForm[f.key]}
                                            onChange={e => setAddForm(p => ({ ...p, [f.key]: e.target.value }))}
                                            style={inputStyle} />
                                    )}
                                </div>
                            ))}

                            {addResult && (
                                <div style={{
                                    padding: "10px 14px", borderRadius: 8,
                                    background: addResult.ok ? t.accent.greenBg : t.accent.redBg,
                                    border: `1px solid ${addResult.ok ? t.accent.green : t.accent.red}44`,
                                    color: addResult.ok ? t.accent.green : t.accent.red, fontSize: 13
                                }}>
                                    {addResult.ok ? (
                                        <>✅ Kaydedildi — Net: {addResult.net_hours}sa, Fazla Mesai: {addResult.overtime_hours}sa</>
                                    ) : (
                                        <>❌ {addResult.error}</>
                                    )}
                                </div>
                            )}

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                                <button onClick={() => setShowAddModal(false)} style={{
                                    padding: "9px 18px", borderRadius: 8, border: `1px solid ${t.border.input}`,
                                    background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13,
                                }}>Kapat</button>
                                <button onClick={handleAddAttendance} disabled={addLoading} style={{
                                    padding: "9px 20px", borderRadius: 8, border: "none",
                                    background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                                    color: "#fff", cursor: addLoading ? "default" : "pointer", fontSize: 13, fontWeight: 600,
                                }}>{addLoading
                                    ? getLang().hr?.attendanceUi?.saving
                                    : getLang().hr?.attendanceUi?.save}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── OTOMATİK BORDRO BİLEŞENİ ─────────────────────────────────────────────
const AutoPayrollTab = ({ t }) => {
    const now = new Date();
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const [payroll, setPayroll] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const MONTHS = getLang().hr?.payrollUi?.months || [];

    const fetchPayroll = async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/payroll/auto/${month}/${year}`, { headers: headers() });
            if (r.ok) setPayroll(await r.json());
        } catch { }
        setLoading(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await fetch(`${API}/payroll/save/${month}/${year}`, { method: "POST", headers: headers() });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch { }
        setSaving(false);
    };

    const money = (v) => v != null ? `${Number(v).toLocaleString("tr-TR")} ₺` : "—";

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Kontroller */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{
                    padding: "9px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`,
                    borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none"
                }}>
                    {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
                <select value={year} onChange={e => setYear(Number(e.target.value))} style={{
                    padding: "9px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`,
                    borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none"
                }}>
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <button onClick={fetchPayroll} disabled={loading} style={{
                    padding: "9px 20px", borderRadius: 8, border: "none", cursor: "pointer",
                    background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", fontSize: 13, fontWeight: 600,
                }}>{getLang().hr?.payrollUi?.calculatePayroll}</button>
                {payroll && (
                    <button onClick={handleSave} disabled={saving} style={{
                        padding: "9px 20px", borderRadius: 8, border: "none", cursor: "pointer",
                        background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff", fontSize: 13, fontWeight: 600,
                    }}>{saving ? getLang().hr?.payrollUi?.saving : getLang().hr?.payrollUi?.savePayroll}</button>
                )}
                {saved && (
                    <span style={{ fontSize: 13, color: t.accent.green, fontWeight: 600 }}>✅ Kaydedildi!</span>
                )}
            </div>

            {/* Bilgi Notu */}
            <div style={{
                padding: "12px 16px", background: t.accent.blueBg,
                border: `1px solid ${t.accent.blueBorder}`, borderRadius: 10, fontSize: 12, color: t.accent.blue
            }}>
                {getLang().hr?.payrollUi?.payrollInfo}
                {getLang().hr?.payrollUi?.payrollRatesInfo}
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0", color: t.text.muted }}>{getLang().hr?.payrollUi?.calculating}</div>
            ) : !payroll ? (
                <div style={{ textAlign: "center", padding: "60px 0", color: t.text.placeholder }}>
                    {getLang().hr?.payrollUi?.selectMonthHint}
                </div>
            ) : (
                <>
                    {/* Özet Kartları */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
                        {[
                            { label: getLang().hr?.payrollUi?.totalGross, value: money(payroll.total_gross), color: t.accent.blue },
                            { label: getLang().hr?.payrollUi?.totalNet, value: money(payroll.total_net), color: t.accent.green },
                            { label: getLang().hr?.payrollUi?.totalEmployerCost, value: money(payroll.total_employer_cost), color: t.accent.red },
                        ].map(({ label, value, color }) => (
                            <div key={label} style={{
                                background: t.bg.card, border: `1px solid ${t.border.card}`,
                                borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card
                            }}>
                                <div style={{
                                    fontSize: 11, color: t.text.muted, marginBottom: 8,
                                    textTransform: "uppercase", fontWeight: 600
                                }}>{label}</div>
                                <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Detay Tablosu */}
                    <div style={{
                        background: t.bg.card, border: `1px solid ${t.border.card}`,
                        borderRadius: 14, overflow: "auto", boxShadow: t.shadow.card
                    }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                            <thead>
                                <tr style={{ background: t.bg.theadRow }}>
                                    {[
                                    getLang().hr?.payrollUi?.personnel,
                                    getLang().hr?.payrollUi?.departmentShort,
                                    getLang().hr?.payrollUi?.workDays,
                                    getLang().hr?.payrollUi?.absent,
                                    getLang().hr?.payrollUi?.workHoursShort,
                                    getLang().hr?.payrollUi?.overtimeHoursShort,
                                    getLang().hr?.payrollUi?.gross,
                                    getLang().hr?.payrollUi?.absenceDeductionShort,
                                    getLang().hr?.payrollUi?.employeeSgk,
                                    getLang().hr?.payrollUi?.unemployment,
                                    getLang().hr?.payrollUi?.incomeTaxShort,
                                    getLang().hr?.payrollUi?.stampTaxShort,
                                    getLang().hr?.payrollUi?.net,
                                    getLang().hr?.payrollUi?.employerSgk
                                  ].map(h => (
                                        <th key={h} style={{
                                            padding: "10px 10px", textAlign: "left", fontSize: 9,
                                            fontWeight: 700, color: t.text.label, textTransform: "uppercase",
                                            whiteSpace: "nowrap", letterSpacing: "0.04em"
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(payroll.payroll || []).map((r, i) => (
                                    <tr key={i} style={{
                                        borderTop: `1px solid ${t.border.subtle}`,
                                        background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row
                                    }}>
                                        <td style={{ padding: "10px 10px", fontWeight: 600, color: t.text.primary, fontSize: 12, whiteSpace: "nowrap" }}>
                                            {r.full_name}
                                        </td>
                                        <td style={{ padding: "10px 10px", fontSize: 11, color: t.text.muted }}>{r.department || "—"}</td>
                                        <td style={{ padding: "10px 10px", fontWeight: 700, color: t.accent.blue, fontSize: 12 }}>{r.work_days}</td>
                                        <td style={{
                                            padding: "10px 10px", fontWeight: 700,
                                            color: r.absent_days > 0 ? t.accent.red : t.text.muted, fontSize: 12
                                        }}>{r.absent_days}</td>
                                        <td style={{ padding: "10px 10px", color: t.accent.green, fontSize: 12 }}>{r.total_worked_hours}</td>
                                        <td style={{ padding: "10px 10px", color: t.accent.yellow, fontSize: 12 }}>{r.total_overtime_hours}</td>
                                        <td style={{ padding: "10px 10px", fontWeight: 600, color: t.accent.blue, fontSize: 12 }}>{money(r.gross_pay)}</td>
                                        <td style={{ padding: "10px 10px", color: r.absent_deduction > 0 ? t.accent.red : t.text.muted, fontSize: 12 }}>
                                            {r.absent_deduction > 0 ? `-${money(r.absent_deduction)}` : "—"}
                                        </td>
                                        <td style={{ padding: "10px 10px", color: t.accent.yellow, fontSize: 12 }}>{money(r.sgk_employee)}</td>
                                        <td style={{ padding: "10px 10px", color: t.accent.yellow, fontSize: 12 }}>{money(r.unemployment_employee)}</td>
                                        <td style={{ padding: "10px 10px", color: t.accent.red, fontSize: 12 }}>{money(r.income_tax)}</td>
                                        <td style={{ padding: "10px 10px", color: t.accent.red, fontSize: 12 }}>{money(r.stamp_tax)}</td>
                                        <td style={{ padding: "10px 10px", fontWeight: 800, color: t.accent.green, fontSize: 13 }}>{money(r.net_pay)}</td>
                                        <td style={{ padding: "10px 10px", color: t.accent.purple, fontSize: 12 }}>{money(r.sgk_employer)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function HR() {
  const { t, themeName } = useTheme();
  const { lang } = useLanguage();
  void lang;
  const [mainTab, setMainTab] = useState(localStorage.getItem("tab_hr") || "employees"); // employees | attendance | payroll
  const [employees, setEmployees] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState([]);
  const [, setPayrollSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [view, setView] = useState(localStorage.getItem("view_hr") || "grid");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stats, setStats] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Önce users → personnel senkronize et
      await fetch(`${API}/hr/sync-users`, { method: "POST", headers: headers() }).catch(() => {});

      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/hr/employees`, { headers: headers() }),
        fetch(`${API}/hr/attendance/today`, { headers: headers() }),
        fetch(`${API}/hr/payroll/summary`, { headers: headers() }),
      ]);
      if (r1.ok) {
        const data = await r1.json();
        const list = Array.isArray(data) ? data : data.items || [];
        setEmployees(list);
        const active = list.filter(e => e.status === "active").length;
        const onLeave = list.filter(e => e.status === "leave").length;
        const totalPayroll = list.reduce((s, e) => s + (e.gross_salary || 0), 0);
        setStats(p => ({ ...p, total: list.length, active, onLeave, totalPayroll }));
      } else throw new Error(getLang().hr?.employeeForm?.employeeLoadFailed);
      if (r2.ok) {
        const data = await r2.json();
        setAttendanceSummary(Array.isArray(data) ? data : data.items || []);
        const present = (Array.isArray(data) ? data : data.items || []).filter(a => a.status === "present" || a.status === "late").length;
        setStats(p => ({ ...p, presentToday: present }));
      }
      if (r3.ok) {
        const data = await r3.json();
        setPayrollSummary(Array.isArray(data) ? data : data.items || []);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = employees.filter(e => {
    const matchSearch = !search ||
      (e.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.employee_no || "").toLowerCase().includes(search.toLowerCase());
    const matchDept = filterDept === "all" || e.department === filterDept;
    const matchStatus = filterStatus === "all" || e.status === filterStatus;
    return matchSearch && matchDept && matchStatus;
  });

  // Departman dağılımı grafiği
  const deptData = DEPARTMENTS.map(d => ({
    name: d, count: employees.filter(e => e.department === d).length,
  })).filter(d => d.count > 0);

  const selectStyle = {
    padding: "10px 14px", background: t.bg.input,
    border: `1px solid ${t.border.input}`, borderRadius: 10,
    color: t.text.primary, fontSize: 13, outline: "none", cursor: "pointer",
  };

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary,
      fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes sf-spin { to{transform:rotate(360deg)} }
      `}</style>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>
              👥 {getLang().hr?.title}
            </h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {getLang().hr?.subtitle}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10,
              border: `1px solid ${t.border.input}`, background: t.bg.toggle,
              color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄 {getLang().hr?.refresh}</button>
            {mainTab === "employees" && (
              <button onClick={() => { setEditing(null); setShowForm(true); }} style={{
                padding: "10px 20px", borderRadius: 10, border: "none",
                background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: t.shadow.btn,
              }}>{`+ ${getLang().hr?.addEmployee}`}</button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: getLang().hr?.totalEmployees, value: stats.total || 0, color: t.accent.blue, icon: "👥" },
            { label: getLang().hr?.activeEmployees, value: stats.active || 0, color: t.accent.green, icon: "✅" },
            { label: getLang().hr?.presentToday, value: stats.presentToday || 0, color: t.accent.yellow, icon: "📋" },
            { label: getLang().hr?.onLeaveEmployees, value: stats.onLeave || 0, color: t.accent.purple, icon: "🌴" },
            { label: getLang().hr?.monthlyPayroll, value: stats.totalPayroll ? `${(stats.totalPayroll / 1000).toFixed(0)}K ₺` : "0 ₺", color: t.accent.red, icon: "💰" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, minWidth: 130, background: t.bg.card,
              border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 12, boxShadow: t.shadow.card }}>
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Ana Sekmeler */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20,
          background: t.bg.toggle, border: `1px solid ${t.border.input}`,
          borderRadius: 12, padding: 4, width: "fit-content" }}>
          {[
            ["employees", getLang().hr?.mainTabs?.employees],
            ["attendance", getLang().hr?.mainTabs?.attendance],
            ["payroll", getLang().hr?.mainTabs?.payroll],
            ["competency", getLang().hr?.mainTabs?.competency],
            ["training", getLang().hr?.mainTabs?.training],
            ["performance", getLang().hr?.mainTabs?.performance],
            ["orgchart", getLang().hr?.mainTabs?.orgchart],
            ["worksettings", getLang().hr?.mainTabs?.worksettings],
            ["timesheet", getLang().hr?.mainTabs?.timesheet],
            ["autopayroll", getLang().hr?.mainTabs?.autopayroll],
          ].map(([v, label]) => (
            <button key={v} onClick={() => { setMainTab(v); localStorage.setItem("tab_hr", v); setSearch(""); }} style={{
              padding: "9px 18px", borderRadius: 9, border: "none", cursor: "pointer",
              background: mainTab === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none",
              color: mainTab === v ? t.text.primary : t.text.muted,
              fontSize: 13, fontWeight: 600,
              boxShadow: mainTab === v ? t.shadow.card : "none", transition: "all 0.2s",
            }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`,
              borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{getLang().hr?.loading}</span>
          </div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: "center", background: t.accent.redBg,
            border: `1px solid ${t.accent.red}44`, borderRadius: 14, color: t.accent.red }}>
            ⚠️ {error}
            <br />
            <button onClick={fetchData} style={{ marginTop: 12, padding: "8px 16px", borderRadius: 8,
              border: `1px solid ${t.accent.red}44`, background: "none", color: t.accent.red, cursor: "pointer" }}>
              Tekrar Dene
            </button>
          </div>
        ) : (
          <>
            {/* ── ÇALIŞANLAR ── */}
            {mainTab === "employees" && (
              <>
                <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
                  <input placeholder={getLang().hr?.employeeSearchPlaceholder} value={search}
                    onChange={e => setSearch(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 220 }} />
                  <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={selectStyle}>
                    <option value="all">{getLang().hr?.allDepartments}</option>
                    {DEPARTMENTS.map(d => (
                      <option key={d} value={d}>
                        {getDepartmentLabel(d)}
                      </option>
                    ))}
                  </select>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
                    <option value="all">{getLang().hr?.allStatuses}</option>
                    {Object.entries(getEmpStatus()).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                  <div style={{ display: "flex", gap: 4, background: t.bg.toggle,
                    border: `1px solid ${t.border.input}`, borderRadius: 10, padding: 4 }}>
                    {[["grid","⊞"],["list","≡"]].map(([v, icon]) => (
                      <button key={v} onClick={() => { setView(v); localStorage.setItem("view_hr", v); }} style={{
                        padding: "6px 12px", borderRadius: 7, border: "none", cursor: "pointer",
                        background: view === v ? t.bg.toggleActive : "none",
                        color: view === v ? t.text.primary : t.text.muted, fontSize: 16,
                      }}>{icon}</button>
                    ))}
                  </div>
                </div>

                {view === "grid" ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
                    {filtered.length === 0
                      ? <div style={{ gridColumn: "1/-1", textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>
                        {employees.length === 0 ? getLang().hr?.noEmployees : getLang().hr?.noMatchingEmployees}
                      </div>
                      : filtered.map((emp, idx) => {
                        const c = scEmp(emp.status, themeName);
                        return (
                          <div key={emp.id || idx} onClick={() => setSelected(emp)} style={{
                            background: t.bg.card, border: `1px solid ${t.border.card}`,
                            borderRadius: 14, padding: 18, cursor: "pointer",
                            transition: "all 0.2s", boxShadow: t.shadow.card,
                          }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = `${c.color}55`; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = t.border.card; }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                              <Avatar name={emp.full_name} t={t} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {emp.full_name}
                                </div>
                                <div style={{ fontSize: 12, color: t.text.muted }}>{getTitleLabel(emp.title || emp.position)}</div>
                              </div>
                              <EmpBadge status={emp.status} themeName={themeName} />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                              {[
                                { l: getLang().hr?.employeeDetail?.department, v: getDepartmentLabel(emp.department) },
                                { l: getLang().hr?.employeeDetail?.employeeNo, v: emp.employee_no || "—" },
                                { l: getLang().hr?.employeeDetail?.shift, v: getShiftLabel(emp.shift) },
                                { l: getLang().hr?.employeeDetail?.seniority, v: emp.hire_date ? `${((new Date() - new Date(emp.hire_date)) / (365.25 * 24 * 3600 * 1000)).toFixed(1)}y` : "—" },
                              ].map(({ l, v }) => (
                                <div key={l} style={{ background: t.bg.metricCard, borderRadius: 8,
                                  padding: "7px 10px", border: `1px solid ${t.border.subtle}` }}>
                                  <div style={{ fontSize: 9, color: t.text.muted, textTransform: "uppercase", marginBottom: 2 }}>{l}</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: t.text.primary }}>{v}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                    borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: t.bg.theadRow }}>
                          {[
                          getLang().hr?.employeeDetail?.employee,
                          getLang().hr?.employeeDetail?.employeeNo,
                          getLang().hr?.employeeDetail?.department,
                          getLang().hr?.employeeDetail?.position,
                          getLang().hr?.employeeDetail?.shift,
                          getLang().hr?.employeeDetail?.hireDate,
                          getLang().hr?.employeeDetail?.grossSalary,
                          getLang().hr?.employeeDetail?.status,
                          getLang().hr?.employeeDetail?.action
                        ].map(h => (
                            <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11,
                              fontWeight: 700, color: t.text.label, textTransform: "uppercase",
                              letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0
                          ? <tr><td colSpan={9} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{getLang().hr?.employeeDetail?.noEmployee}</td></tr>
                          : filtered.map((emp, i) => (
                            <tr key={emp.id || i} style={{ borderTop: `1px solid ${t.border.subtle}`,
                              background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                              <td style={{ padding: "12px 14px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  <Avatar name={emp.full_name} size={30} t={t} />
                                  <span style={{ fontWeight: 600, color: t.text.primary, fontSize: 13 }}>{emp.full_name}</span>
                                </div>
                              </td>
                              <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{emp.employee_no || "—"}</td>
                              <td style={{ padding: "12px 14px", fontSize: 13, color: t.text.secondary }}>{getDepartmentLabel(emp.department)}</td>
                              <td style={{ padding: "12px 14px", fontSize: 13, color: t.text.secondary }}>{getTitleLabel(emp.title || emp.position)}</td>
                              <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{getShiftLabel(emp.shift)}</td>
                              <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{fmt(emp.hire_date)}</td>
                              <td style={{ padding: "12px 14px", fontWeight: 600, color: t.accent.green, fontSize: 13 }}>{money(emp.gross_salary)}</td>
                              <td style={{ padding: "12px 14px" }}><EmpBadge status={emp.status} themeName={themeName} /></td>
                              <td style={{ padding: "12px 14px" }}>
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button onClick={() => setSelected(emp)} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                                    background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, color: t.accent.blue }}>{getLang().detail}</button>
                                  <button onClick={() => { setEditing(emp); setShowForm(true); }} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                                    background: t.bg.toggle, border: `1px solid ${t.border.input}`, color: t.text.secondary }}>✏️</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* ── DEVAM TAKİBİ ── */}
            {mainTab === "attendance" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Bugün özeti */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
                  {Object.entries(getAttendStatus()).map(([k, v]) => {
                    const count = attendanceSummary.filter(a => a.status === k).length;
                    return (
                      <div key={k} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                        borderRadius: 12, padding: "14px 16px", boxShadow: t.shadow.card }}>
                        <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>{v.label}</div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: v.color }}>{count}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Devam listesi */}
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${t.border.default}`,
                    fontSize: 14, fontWeight: 700, color: t.text.primary }}>
                    {getLang().hr?.attendanceUi?.todayAttendance}
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: t.bg.theadRow }}>
                        {[
                          getLang().hr?.attendanceUi?.employee,
                          getLang().hr?.attendanceUi?.department,
                          getLang().hr?.attendanceUi?.checkIn,
                          getLang().hr?.attendanceUi?.checkOut,
                          getLang().hr?.attendanceUi?.working,
                          getLang().hr?.attendanceUi?.status
                        ].map(h => (
                          <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 11,
                            fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceSummary.length === 0
                        ? <tr><td colSpan={6} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>
                          {getLang().hr?.attendanceUi?.noTodayAttendance}
                        </td></tr>
                        : attendanceSummary.map((a, i) => {
                          const sc_ = getAttendStatus()[a.status] || getAttendStatus().present;
                          return (
                            <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}`,
                              background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                              <td style={{ padding: "11px 14px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <Avatar name={a.full_name} size={28} t={t} />
                                  <span style={{ fontSize: 13, fontWeight: 600, color: t.text.primary }}>{a.full_name}</span>
                                </div>
                              </td>
                              <td style={{ padding: "11px 14px", fontSize: 12, color: t.text.muted }}>{getDepartmentLabel(a.department)}</td>
                              <td style={{ padding: "11px 14px", fontSize: 13, color: t.text.secondary }}>{a.check_in || "—"}</td>
                              <td style={{ padding: "11px 14px", fontSize: 13, color: t.text.secondary }}>{a.check_out || "—"}</td>
                              <td style={{ padding: "11px 14px", fontWeight: 600, color: t.accent.blue, fontSize: 13 }}>
                                {a.work_hours ? `${a.work_hours}h` : "—"}
                              </td>
                              <td style={{ padding: "11px 14px" }}>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                                  color: sc_.color, background: `${sc_.color}18` }}>{sc_.label}</span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {/* Departman devam grafiği */}
                {deptData.length > 0 && (
                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                    borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>
                      {getLang().hr?.attendanceUi?.departmentDistribution}
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={deptData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                        <XAxis dataKey="name" stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} />
                        <YAxis stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} />
                        <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                        <Bar dataKey="count" fill={t.accent.blue} radius={[4, 4, 0, 0]} name={getLang().hr?.attendanceUi?.employeeSeries} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {/* ── BORDRO ── */}
            {mainTab === "payroll" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Özet kartlar */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
                  {[
                    { label: getLang().hr?.payrollUi?.totalGross, value: money(employees.reduce((s, e) => s + (e.gross_salary || 0), 0)), color: t.accent.blue },
                    { label: getLang().hr?.payrollUi?.totalEmployerSgk, value: money(employees.reduce((s, e) => s + ((e.gross_salary || 0) * 0.2075), 0)), color: t.accent.yellow },
                    { label: getLang().hr?.payrollUi?.totalEmployeeSgk, value: money(employees.reduce((s, e) => s + ((e.gross_salary || 0) * 0.14), 0)), color: t.accent.purple },
                    { label: getLang().hr?.payrollUi?.estimatedTotalNet, value: money(employees.reduce((s, e) => s + ((e.gross_salary || 0) * 0.72), 0)), color: t.accent.green },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                      borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card }}>
                      <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 8, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Bordro tablosu */}
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${t.border.default}`,
                    display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>{getLang().hr?.payrollUi?.monthlyPayrollList}</div>
                    <div style={{ fontSize: 12, color: t.text.muted }}>{getLang().hr?.payrollUi?.estimatedCalculationNote}</div>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: t.bg.theadRow }}>
                        {[
                          getLang().hr?.payrollUi?.employee,
                          getLang().hr?.payrollUi?.department,
                          getLang().hr?.payrollUi?.grossSalary,
                          getLang().hr?.payrollUi?.employeeSgkRate,
                          getLang().hr?.payrollUi?.incomeTaxRate,
                          getLang().hr?.payrollUi?.netSalary,
                          getLang().hr?.payrollUi?.employerSgkRate
                        ].map(h => (
                          <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11,
                            fontWeight: 700, color: t.text.label, textTransform: "uppercase",
                            letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {employees.filter(e => e.status === "active" && e.gross_salary).length === 0
                        ? <tr><td colSpan={7} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>
                          Aktif çalışan bordro verisi bulunamadı
                        </td></tr>
                        : employees.filter(e => e.status === "active").map((emp, i) => {
                          const gross = emp.gross_salary || 0;
                          const sgkEmp = gross * 0.14;
                          const tax = gross * 0.15;
                          const net = gross - sgkEmp - tax;
                          const sgkEmr = gross * 0.2075;
                          return (
                            <tr key={emp.id || i} style={{ borderTop: `1px solid ${t.border.subtle}`,
                              background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                              <td style={{ padding: "12px 14px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <Avatar name={emp.full_name} size={28} t={t} />
                                  <span style={{ fontSize: 13, fontWeight: 600, color: t.text.primary }}>{emp.full_name}</span>
                                </div>
                              </td>
                              <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{getDepartmentLabel(emp.department)}</td>
                              <td style={{ padding: "12px 14px", fontWeight: 600, color: t.accent.blue, fontSize: 13 }}>{money(gross)}</td>
                              <td style={{ padding: "12px 14px", color: t.accent.yellow, fontSize: 13 }}>{money(sgkEmp)}</td>
                              <td style={{ padding: "12px 14px", color: t.accent.red, fontSize: 13 }}>{money(tax)}</td>
                              <td style={{ padding: "12px 14px", fontWeight: 700, color: t.accent.green, fontSize: 13 }}>{money(net)}</td>
                              <td style={{ padding: "12px 14px", color: t.accent.purple, fontSize: 13 }}>{money(sgkEmr)}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {/* Bilgi notu */}
                <div style={{ padding: "12px 18px", background: t.accent.blueBg,
                  border: `1px solid ${t.accent.blueBorder}`, borderRadius: 10,
                  fontSize: 12, color: t.accent.blue }}>
                  {getLang().hr?.payrollUi?.disclaimer} 
                  {getLang().hr?.payrollUi?.legislationNote}
                </div>
              </div>
            )}

            {/* ── YETKİNLİK MATRİSİ ── */}
            {mainTab === "competency" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ padding: "12px 18px", background: t.accent.blueBg,
                  border: `1px solid ${t.accent.blueBorder}`, borderRadius: 10, fontSize: 13, color: t.accent.blue }}>
                  {getLang().hr?.competencyUi?.intro}
                </div>

                {/* Yetki Seviyeleri Açıklaması */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {[
                    { level: getLang().hr?.competencyUi?.levels?.master, color: t.accent.green, desc: getLang().hr?.competencyUi?.levelDescriptions?.master },
                    { level: getLang().hr?.competencyUi?.levels?.basic, color: t.accent.blue, desc: getLang().hr?.competencyUi?.levelDescriptions?.basic },
                    { level: getLang().hr?.competencyUi?.levels?.trainee, color: t.accent.yellow, desc: getLang().hr?.competencyUi?.levelDescriptions?.trainee },
                    {
                      level: getLang().hr?.competencyUi?.levels?.none,
                      color: t.text.muted,
                      desc: getLang().hr?.competencyUi?.levelDescriptions?.none
                    },
                    { level: getLang().hr?.competencyUi?.levels?.expired, color: t.accent.red, desc: getLang().hr?.competencyUi?.levelDescriptions?.expired },
                  ].map(({ level, color, desc }) => (
                    <div key={level} style={{ display: "flex", alignItems: "center", gap: 8,
                      background: t.bg.metricCard, border: `1px solid ${t.border.subtle}`,
                      borderRadius: 8, padding: "6px 12px" }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color }}>{level}</span>
                      <span style={{ fontSize: 11, color: t.text.muted }}>— {desc}</span>
                    </div>
                  ))}
                </div>

                {/* Matris Tablosu */}
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, overflow: "auto", boxShadow: t.shadow.card }}>
                  <table style={{ borderCollapse: "collapse", minWidth: 700 }}>
                    <thead>
                      <tr style={{ background: t.bg.theadRow }}>
                        <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12,
                          fontWeight: 700, color: t.text.label, whiteSpace: "nowrap",
                          borderRight: `1px solid ${t.border.default}`, minWidth: 160 }}>{getLang().hr?.competencyUi?.employee}</th>
                        {[
                          getLang().hr?.competencyUi?.skills?.cnc,
                          getLang().hr?.competencyUi?.skills?.lathe,
                          getLang().hr?.competencyUi?.skills?.milling,
                          getLang().hr?.competencyUi?.skills?.welding,
                          getLang().hr?.competencyUi?.skills?.forklift,
                          getLang().hr?.competencyUi?.skills?.crane,
                          getLang().hr?.competencyUi?.skills?.press,
                          getLang().hr?.competencyUi?.skills?.qualityEquipment
                        ].map(m => (
                          <th key={m} style={{ padding: "12px 14px", textAlign: "center", fontSize: 11,
                            fontWeight: 700, color: t.text.label, textTransform: "uppercase",
                            letterSpacing: "0.04em", whiteSpace: "nowrap", minWidth: 100 }}>{m}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {employees.filter(e => e.status === "active").length === 0 ? (
                        <tr><td colSpan={9} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>
                          {getLang().hr?.competencyUi?.noActiveEmployee}
                        </td></tr>
                      ) : employees.filter(e => e.status === "active").map((emp, i) => {
                        const competencies = emp.competencies || {};
                        const getLevelColor = (level) => {
                          if (!level || level === "none") return t.text.muted;
                          if (level === "master") return t.accent.green;
                          if (level === "basic") return t.accent.blue;
                          if (level === "trainee") return t.accent.yellow;
                          if (level === "expired") return t.accent.red;
                          return t.text.muted;
                        };
                        const getLevelLabel = (level) => {
                          if (!level || level === "none") return "—";
                          if (level === "master") return getLang().hr?.competencyUi?.levels?.master;
                          if (level === "basic") return getLang().hr?.competencyUi?.levels?.basic;
                          if (level === "trainee") return getLang().hr?.competencyUi?.levels?.trainee;
                          if (level === "expired") return getLang().hr?.competencyUi?.expiredDisplay;
                          return "—";
                        };
                        const machines = ["cnc", "lathe", "milling", "welding", "forklift", "crane", "press", "quality"];
                        return (
                          <tr key={emp.id || i} style={{ borderTop: `1px solid ${t.border.subtle}`,
                            background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                            <td style={{ padding: "12px 16px", borderRight: `1px solid ${t.border.default}` }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <Avatar name={emp.full_name} size={28} t={t} />
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: t.text.primary }}>{emp.full_name}</div>
                                  <div style={{ fontSize: 11, color: t.text.muted }}>{getDepartmentLabel(emp.department)}</div>
                                </div>
                              </div>
                            </td>
                            {machines.map(m => {
                              const level = competencies[m] || "none";
                              const color = getLevelColor(level);
                              return (
                                <td key={m} style={{ padding: "10px 14px", textAlign: "center" }}>
                                  <span style={{
                                    fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 20,
                                    color, background: `${color}18`,
                                    border: `1px solid ${color}33`,
                                  }}>{getLevelLabel(level)}</span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Süresi Dolan Sertifikalar Uyarısı */}
                {employees.some(e => e.competencies && Object.values(e.competencies).includes("expired")) && (
                  <div style={{ padding: "14px 20px", background: t.accent.redBg,
                    border: `1px solid ${t.accent.red}44`, borderRadius: 12,
                    color: t.accent.red, fontSize: 13, fontWeight: 600 }}>
                    {getLang().hr?.trainingUi?.expiredWarning}
                  </div>
                )}
              </div>
            )}

            {/* ── EĞİTİM YÖNETİMİ ── */}
            {mainTab === "training" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Eğitim İstatistikleri */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
                  {[
                    { label: getLang().hr?.trainingUi?.plannedTraining, value: 0, color: t.accent.blue, icon: "📅" },
                    { label: getLang().hr?.trainingUi?.completed, value: 0, color: t.accent.green, icon: "✅" },
                    { label: getLang().hr?.trainingUi?.participant, value: 0, color: t.accent.yellow, icon: "👤" },
                    { label: getLang().hr?.trainingUi?.expiredCertificate, value: employees.filter(e => e.competencies && Object.values(e.competencies || {}).includes("expired")).length, color: t.accent.red, icon: "⚠️" },
                  ].map(s => (
                    <div key={s.label} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                      borderRadius: 14, padding: "16px 18px", boxShadow: t.shadow.card,
                      display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 22 }}>{s.icon}</span>
                      <div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 600 }}>{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Eğitim Planı */}
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>{getLang().hr?.trainingUi?.planTitle}</div>
                    <button style={{ padding: "8px 16px", borderRadius: 8, border: "none",
                      background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                      color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                      {getLang().hr?.trainingUi?.addTraining}
                    </button>
                  </div>

                  {/* Eğitim Kategorileri */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
                    {[
                      { label: getLang().hr?.trainingUi?.categories?.safety, icon: "🦺", count: 0, color: t.accent.red },
                      { label: getLang().hr?.trainingUi?.categories?.technical, icon: "⚙️", count: 0, color: t.accent.blue },
                      { label: getLang().hr?.trainingUi?.categories?.quality, icon: "✅", count: 0, color: t.accent.green },
                      { label: getLang().hr?.trainingUi?.categories?.machine, icon: "🏭", count: 0, color: t.accent.yellow },
                      { label: getLang().hr?.trainingUi?.categories?.management, icon: "👔", count: 0, color: t.accent.purple },
                      { label: getLang().hr?.trainingUi?.categories?.other, icon: "📋", count: 0, color: t.text.muted },
                    ].map(cat => (
                      <div key={cat.label} style={{ background: t.bg.metricCard,
                        border: `1px solid ${t.border.default}`, borderRadius: 12, padding: "14px 16px",
                        display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 20 }}>{cat.icon}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: t.text.primary }}>{cat.label}</div>
                          <div style={{ fontSize: 12, color: cat.color, fontWeight: 700 }}>
                          {cat.count} {getLang().hr?.trainingUi?.trainingUnit}
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ textAlign: "center", color: t.text.placeholder, padding: "32px 0",
                    border: `2px dashed ${t.border.default}`, borderRadius: 10 }}>
                    {getLang().hr?.trainingUi?.emptyPlan}<br />
                    <span style={{ fontSize: 12 }}>{getLang().hr?.trainingUi?.backendTrainingNote}</span>
                  </div>
                </div>

                {/* Sertifika Takip Tablosu */}
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${t.border.default}`,
                    fontSize: 14, fontWeight: 700, color: t.text.primary }}>{getLang().hr?.trainingUi?.certificateTracking}</div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: t.bg.theadRow }}>
                        {[
                          getLang().hr?.trainingUi?.employee,
                          getLang().hr?.trainingUi?.department,
                          getLang().hr?.trainingUi?.certificate,
                          getLang().hr?.trainingUi?.issueDate,
                          getLang().hr?.trainingUi?.validity,
                          getLang().hr?.trainingUi?.status
                        ].map(h => (
                          <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 11,
                            fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td colSpan={6} style={{ textAlign: "center", color: t.text.placeholder, padding: 32 }}>
                        {getLang().hr?.trainingUi?.noCertificate}
                      </td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── PERFORMANS DEĞERLENDİRME ── */}
            {mainTab === "performance" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ padding: "12px 18px", background: t.accent.yellowBg,
                  border: `1px solid ${t.accent.yellow}44`, borderRadius: 10, fontSize: 13, color: t.accent.yellow }}>
                  {getLang().hr?.performanceUi?.intro}
                </div>

                {/* Performans Özeti */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
                  {employees.filter(e => e.status === "active").slice(0, 6).map((emp, i) => {
                    const score = emp.performance_score || Math.floor(Math.random() * 30 + 70);
                    const scoreColor = score >= 90 ? t.accent.green : score >= 75 ? t.accent.yellow : t.accent.red;
                    return (
                      <div key={emp.id || i} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                        borderRadius: 14, padding: 18, boxShadow: t.shadow.card }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                          <Avatar name={emp.full_name} size={36} t={t} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{emp.full_name}</div>
                            <div style={{ fontSize: 11, color: t.text.muted }}>
                            {getDepartmentLabel(emp.department)} · {getTitleLabel(emp.title)}
                          </div>
                          </div>
                        </div>

                        {/* Performans Skoru */}
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: t.text.muted, textTransform: "uppercase", fontWeight: 600 }}>
                              {getLang().hr?.performanceUi?.overallScore}
                            </span>
                            <span style={{ fontSize: 16, fontWeight: 800, color: scoreColor }}>{score}/100</span>
                          </div>
                          <div style={{ height: 8, background: t.bg.tag, borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${score}%`, background: scoreColor, borderRadius: 4 }} />
                          </div>
                        </div>

                        {/* Kriter Puanları */}
                        {[
                          { label: getLang().hr?.performanceUi?.attendance, value: emp.attendance_score || 85 },
                          { label: getLang().hr?.performanceUi?.productivity, value: emp.productivity_score || 78 },
                          { label: getLang().hr?.performanceUi?.quality, value: emp.quality_score || 92 },
                        ].map(({ label, value }) => (
                          <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: t.text.muted, width: 70 }}>{label}</span>
                            <div style={{ flex: 1, height: 5, background: t.bg.tag, borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${value}%`,
                                background: value >= 90 ? t.accent.green : value >= 75 ? t.accent.yellow : t.accent.red,
                                borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: t.text.muted, width: 28, textAlign: "right" }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>

                {employees.filter(e => e.status === "active").length === 0 && (
                  <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>
                    {getLang().hr?.performanceUi?.noActiveEmployee}
                  </div>
                )}

                {/* Performans Kriterleri Açıklaması */}
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>
                    {getLang().hr?.performanceUi?.evaluationCriteria}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
                    {[
                      { label: getLang().hr?.performanceUi?.criteria?.attendance, weight: "%30", icon: "📋", desc: getLang().hr?.performanceUi?.criteria?.attendanceDesc },
                      { label: getLang().hr?.performanceUi?.criteria?.productivity, weight: "%40", icon: "⚡", desc: getLang().hr?.performanceUi?.criteria?.productivityDesc },
                      { label: getLang().hr?.performanceUi?.criteria?.quality, weight: "%30", icon: "✅", desc: getLang().hr?.performanceUi?.criteria?.qualityDesc },
                    ].map(({ label, weight, icon, desc }) => (
                      <div key={label} style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`,
                        borderRadius: 12, padding: "16px 18px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          <span style={{ fontSize: 20 }}>{icon}</span>
                          <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{label}</div>
                          <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 800, color: t.accent.blue }}>{weight}</span>
                        </div>
                        <div style={{ fontSize: 12, color: t.text.muted }}>{desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── ORGANİZASYON ŞEMASI ── */}
            {mainTab === "orgchart" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, padding: 28, boxShadow: t.shadow.card }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 24 }}>
                    {getLang().hr?.orgChartUi?.title}
                  </div>

                  {/* Şema Render */}
                  {employees.length === 0 ? (
                    <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>
                      {getLang().hr?.noEmployeeData}
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      {/* Departman Bazlı Gruplama */}
                      {DEPARTMENTS.map(dept => {
                        const deptEmps = employees.filter(e => e.department === dept && e.status === "active");
                        if (deptEmps.length === 0) return null;
                        const manager = deptEmps.find(e => e.role === "manager") || deptEmps[0];
                        const rest = deptEmps.filter(e => e.id !== manager?.id);
                        return (
                          <div key={dept} style={{ marginBottom: 28 }}>
                            {/* Departman Başlığı */}
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                              <div style={{ height: 1, flex: 1, background: t.border.default }} />
                              <span style={{ fontSize: 12, fontWeight: 700, color: t.text.muted,
                                textTransform: "uppercase", letterSpacing: "0.06em",
                                padding: "4px 12px", background: t.bg.tag, borderRadius: 20,
                                border: `1px solid ${t.border.subtle}` }}>{getDepartmentLabel(dept)}</span>
                              <div style={{ height: 1, flex: 1, background: t.border.default }} />
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                              {/* Yönetici */}
                              <div style={{ background: t.accent.blueBg, border: `2px solid ${t.accent.blue}`,
                                borderRadius: 14, padding: "14px 20px", minWidth: 180, textAlign: "center",
                                boxShadow: `0 4px 14px ${t.accent.blue}22` }}>
                                <Avatar name={manager.full_name} size={40} t={t} />
                                <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginTop: 8 }}>{manager.full_name}</div>
                                <div style={{ fontSize: 11, color: t.accent.blue, fontWeight: 600 }}>{manager.title ? getTitleLabel(manager.title) : (getTitleLabel(manager.role) !== manager.role ? getTitleLabel(manager.role) : getRoleLabel(manager.role))}</div>
                              </div>

                              {/* Bağlantı Çizgisi */}
                              {rest.length > 0 && (
                                <div style={{ width: 2, height: 16, background: t.border.default }} />
                              )}

                              {/* Çalışanlar */}
                              {rest.length > 0 && (
                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
                                  {rest.map((emp, i) => (
                                    <div key={emp.id || i} style={{ background: t.bg.metricCard,
                                      border: `1px solid ${t.border.default}`,
                                      borderRadius: 12, padding: "12px 16px", textAlign: "center", minWidth: 140 }}>
                                      <Avatar name={emp.full_name} size={32} t={t} />
                                      <div style={{ fontSize: 12, fontWeight: 600, color: t.text.primary, marginTop: 6 }}>{emp.full_name}</div>
                                      <div style={{ fontSize: 11, color: t.text.muted }}>{emp.title ? getTitleLabel(emp.title) : (getTitleLabel(emp.role) !== emp.role ? getTitleLabel(emp.role) : getRoleLabel(emp.role))}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Departman İstatistikleri */}
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>
                    {getLang().hr?.orgChartUi?.departmentDistribution}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 10 }}>
                    {DEPARTMENTS.map(dept => {
                      const count = employees.filter(e => e.department === dept && e.status === "active").length;
                      if (count === 0) return null;
                      return (
                        <div key={dept} style={{ background: t.bg.metricCard, border: `1px solid ${t.border.subtle}`,
                          borderRadius: 10, padding: "12px 14px",
                          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 13, color: t.text.secondary }}>
                            {getDepartmentLabel(dept)}
                          </span>
                          <span style={{ fontSize: 16, fontWeight: 800, color: t.accent.blue }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

              {mainTab === "worksettings" && <WorkSettingsTab t={t} />}
              {mainTab === "timesheet" && <TimesheetTab t={t} employees={employees} />}
              {mainTab === "autopayroll" && <AutoPayrollTab t={t} />}

        {!loading && !error && mainTab === "employees" && (
          <div style={{ marginTop: 16, color: t.text.placeholder, fontSize: 12, textAlign: "right" }}>
            {filtered.length} / {employees.length} çalışan gösteriliyor
          </div>
        )}
      </div>

      {/* MODALLER */}
      {selected && (
        <EmployeeDetailModal emp={selected} t={t} themeName={themeName}
          onClose={() => setSelected(null)}
          onEdit={(e) => { setEditing(e); setShowForm(true); setSelected(null); }} />
      )}
      {showForm && (
        <EmployeeFormModal data={editing} t={t}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchData(); }} />
      )}
    </div>
  );
}
